import { useCallback, useEffect, useRef, useState } from "react";
import { Building, Floor, from, is } from "@the-real-insight/tri-model";
import { userAPI } from "../../UserAPI";

type QueryFn = (query: any) => Promise<any[] | null | undefined>;

// Floors are listed top-down by level, matching how a building is drawn: the
// topmost storey first, basements (negative levels) last. The order has to be
// applied to the data — the DataTable shim accepts `defaultSortFieldId` but never
// acts on it. Floors without a usable level sort to the end rather than counting
// as level 0, where they would land in the middle of the real storeys.
export const byLevel = (first: any, second: any): number => {
  const firstLevel = Number(first?.level);
  const secondLevel = Number(second?.level);
  const firstValid = Number.isFinite(firstLevel);
  const secondValid = Number.isFinite(secondLevel);

  if (!firstValid || !secondValid) {
    return firstValid === secondValid ? 0 : firstValid ? -1 : 1;
  }

  return secondLevel - firstLevel;
};

const hasText = (value: unknown): boolean =>
  value != null && String(value).length > 0;

/**
 * Loads the buildings and floors behind the Building > Location tab.
 *
 * - Effects depend on the object's primitive identifiers (`id`, `_id`), never on
 *   the object itself. The parent re-creates the object on every render and
 *   after its own `/query`, which used to restart the whole load chain.
 * - Floors are queried by the object's `_id` in parallel with the building
 *   lookup and only re-queried when the resolved building turns out to be a
 *   different one.
 * - Every response is checked against the current request before it is applied,
 *   so a slow answer for an earlier object can never overwrite a newer one.
 */
export function useBuildingLocationData(
  object: any,
  options: { queryFn?: QueryFn; onError?: (error: unknown) => void } = {},
) {
  const queryFn: QueryFn = options.queryFn ?? ((q) => userAPI.query(q));
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const objectRef = useRef(object);
  objectRef.current = object;

  const [buildings, setBuildings] = useState<any[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [floors, setFloors] = useState<any[]>([]);
  const [floorsLoading, setFloorsLoading] = useState(false);
  const floorsRequest = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchFloors = useCallback(async (buildingId: string): Promise<any[]> => {
    const rows = await queryFnRef.current(
      from(Floor.type).where(is("building").equal(`${buildingId}`)),
    );
    return rows && rows.length > 0 ? [...rows].sort(byLevel) : [];
  }, []);

  // Applies a floors promise unless a newer floors request has started since.
  const applyFloors = useCallback(
    async (request: number, pending: Promise<any[]>) => {
      try {
        const rows = await pending;
        if (mounted.current && request === floorsRequest.current) {
          setFloors(rows);
        }
      } catch (error) {
        if (mounted.current && request === floorsRequest.current) {
          setFloors([]);
          onErrorRef.current?.(error);
        }
      } finally {
        if (mounted.current && request === floorsRequest.current) {
          setFloorsLoading(false);
        }
      }
    },
    [],
  );

  const reloadFloors = useCallback(
    async (buildingId?: string) => {
      if (!buildingId) {
        return;
      }
      const request = ++floorsRequest.current;
      setFloorsLoading(true);
      await applyFloors(request, fetchFloors(buildingId));
    },
    [applyFloors, fetchFloors],
  );

  const objectId = object?._id ? String(object._id) : "";
  const objectBusinessId = hasText(object?.id) ? String(object.id) : "";
  const hasObject = !!object;

  useEffect(() => {
    if (!hasObject) {
      return;
    }
    let cancelled = false;
    const obj = objectRef.current;
    const request = ++floorsRequest.current;
    setFloorsLoading(true);

    // Started right away: the floors hang off the object's `_id`, which is
    // usually already known and does not need the building lookup first.
    const prefetchedFloors: Promise<any[]> | null = objectId
      ? fetchFloors(objectId)
      : null;
    if (prefetchedFloors) {
      void applyFloors(request, prefetchedFloors);
    }

    const lookup = async () => {
      try {
        // Both queries start at once; the `id` result keeps its priority but
        // is not held back by a slow or failing `_id` query.
        const settle = (pending: Promise<any[] | null | undefined>) =>
          pending.then(
            (rows) => ({ ok: true as const, rows: rows ?? [] }),
            (error) => ({ ok: false as const, error }),
          );
        const byBusinessId = settle(
          objectBusinessId
            ? queryFnRef.current(
                from(Building.type).where(is("id").equal(obj.id)),
              )
            : Promise.resolve([] as any[]),
        );
        const byObjectId = settle(
          objectId
            ? queryFnRef.current(
                from(Building.type).where(is("_id").equal(obj._id)),
              )
            : Promise.resolve([] as any[]),
        );

        let found: any[] = [];
        const first = await byBusinessId;
        if (first.ok && first.rows.length > 0) {
          found = first.rows;
        } else {
          const second = await byObjectId;
          if (!first.ok && !second.ok) {
            throw first.error;
          }
          found = second.ok ? second.rows : [];
        }
        if (found.length === 0) {
          found = [obj];
        }
        // The address id is not unique: a tenant's copy shares it with the public buildings it was
        // made from (John-Skilton-Straße 2, Würzburg: two public records and the tenant's). The
        // object itself leads — its floors hang off its `_id`, and the public records have none.
        const own = found.findIndex((row) => String(row?._id) === objectId);
        if (own > 0) {
          found = [found[own], ...found.filter((_, index) => index !== own)];
        }
        if (cancelled) {
          return;
        }

        const primary = found[0];
        setBuildings(found);
        setSelectedBuilding(primary ?? null);

        if (!primary?._id) {
          if (request === floorsRequest.current) {
            setFloorsLoading(false);
          }
          return;
        }
        // The prefetch already covers the common case (building == object).
        if (!prefetchedFloors || String(primary._id) !== objectId) {
          await reloadFloors(primary._id);
        }
      } catch (error) {
        if (!cancelled && request === floorsRequest.current) {
          setFloorsLoading(false);
          onErrorRef.current?.(error);
        }
      }
    };

    void lookup();

    return () => {
      cancelled = true;
    };
  }, [hasObject, objectId, objectBusinessId, fetchFloors, applyFloors, reloadFloors]);

  /** Selects a building (map click) and loads its floors. */
  const selectBuilding = useCallback(
    (building: any) => {
      setSelectedBuilding(building ?? null);
      if (building?._id) {
        void reloadFloors(building._id);
      }
    },
    [reloadFloors],
  );

  return {
    buildings,
    selectedBuilding,
    selectBuilding,
    floors,
    floorsLoading,
    reloadFloors,
  };
}
